import moment from "moment";
import { numberFormat } from "../../../../../../util/currency-formatter";
import { parseFloatValue } from "../../../../../../util/common";

/**
 * costBreakdownCalculations - Calculation functions for cost breakdown
 * 
 * SUMMARY:
 * Contains calculation functions for labor costs, allocation costs, and total costs.
 * Handles overtime calculations and cost aggregations.
 * 
 * MODIFICATIONS:
 * - Extracted from cost-breakdown.jsx to improve separation of concerns
 * - Functions remain pure and testable
 */

/**
 * Calculates the total cost for a resource allocation
 * 
 * Calculates labor cost including normal hours, overtime 1, and overtime 2.
 * Uses global settings for overtime rates (defaults: 1.5x and 2x).
 * 
 * @param {Object} allocation - Resource allocation object
 * @param {string|Date} allocation.approvedStartTime - Start time of allocation
 * @param {string|Date} allocation.approvedEndTime - End time of allocation
 * @param {number} allocation.hourlyRate - Hourly rate for the resource
 * @param {number} [allocation.overtime_1=0] - Overtime 1 hours
 * @param {number} [allocation.overtime_2=0] - Overtime 2 hours
 * @param {Object} globalSettings - Global settings object
 * @param {number} [globalSettings.overtime_1_rate=1.5] - Overtime 1 multiplier
 * @param {number} [globalSettings.overtime_2_rate=2] - Overtime 2 multiplier
 * @returns {number} Total cost as a parsed float value
 */
export const getAllocationCost = (allocation, globalSettings = {}) => {
  const startTime = moment(allocation.approvedStartTime);
  const endTime = moment(allocation.approvedEndTime);
  // Calculate hours directly as the difference in hours between timestamps
  const hours = endTime.diff(startTime, 'hours', true);

  const hourlyRate = allocation.hourlyRate;
  const overtime1 = allocation.overtime_1 || 0;
  const overtime2 = allocation.overtime_2 || 0;
  const overtime1Rate = globalSettings.overtime_1_rate || 1.5;
  const overtime2Rate = globalSettings.overtime_2_rate || 2;

  // Calculate normal hours by subtracting overtime hours from total hours
  const normalHours = hours - (overtime1 + overtime2);

  const overtime1Cost = overtime1 * overtime1Rate * hourlyRate;
  const overtime2Cost = overtime2 * overtime2Rate * hourlyRate;
  const normalCost = normalHours * hourlyRate;

  const totalCost = overtime1Cost + overtime2Cost + normalCost;
  return parseFloatValue(numberFormat(totalCost, true));
};

/**
 * Calculates the total cost from cost details array
 * 
 * Sums up all subTotal values from the cost details array.
 * 
 * @param {Array} costDetails - Array of cost detail objects
 * @param {number} [costDetails[].subTotal=0] - Subtotal for each cost item
 * @returns {number} Total cost sum
 */
export const getTotalCost = (costDetails) => {
  if (!Array.isArray(costDetails)) return 0;
  return costDetails.reduce((sum, item) => sum + (item.subTotal || 0), 0);
};
